// Monta o documento HTML que roda no iframe do artefato (porta do render.py do
// benchmark de artefatos). O iframe usa sandbox SEM allow-same-origin, então o
// código roda numa origem opaca: não lê cookies/tokens do chat nem chama a API.
// Consequência: localStorage/sessionStorage lançam SecurityError — injetamos um
// substituto em memória para a página não quebrar.

export type ArtifactType = 'html' | 'react' | 'svg' | 'mermaid';

// Atributo sandbox comum a todos os iframes de artefato.
export const ARTIFACT_SANDBOX = 'allow-scripts allow-forms allow-modals allow-popups allow-downloads';

const REACT = 'https://unpkg.com/react@18.3.1/umd/react.production.min.js';
const REACT_DOM = 'https://unpkg.com/react-dom@18.3.1/umd/react-dom.production.min.js';
const PROP_TYPES = 'https://unpkg.com/prop-types@15.8.1/prop-types.min.js';
const RECHARTS = 'https://unpkg.com/recharts@2.12.7/umd/Recharts.js';
const BABEL = 'https://unpkg.com/@babel/standalone@7.24.7/babel.min.js';
const TAILWIND = 'https://cdn.tailwindcss.com/3.4.5';
const MERMAID = 'https://cdn.jsdelivr.net/npm/mermaid@10.9.1/dist/mermaid.min.js';

const STORAGE_SHIM = `<script>(function(){function mk(){var d={};return{getItem:function(k){return Object.prototype.hasOwnProperty.call(d,k)?d[k]:null},setItem:function(k,v){d[k]=String(v)},removeItem:function(k){delete d[k]},clear:function(){d={}},key:function(i){return Object.keys(d)[i]||null},get length(){return Object.keys(d).length}}}
['localStorage','sessionStorage'].forEach(function(n){try{window[n].getItem('x')}catch(e){try{Object.defineProperty(window,n,{value:mk(),configurable:true})}catch(_){}}});})();</script>`;

// Erros de execução aparecem numa faixa no rodapé em vez de uma tela branca.
const ERROR_OVERLAY = `<style>#__artifact_err{position:fixed;left:0;right:0;bottom:0;z-index:2147483647;background:#fef2f2;color:#991b1b;font:12px/1.45 ui-monospace,monospace;padding:8px 12px;white-space:pre-wrap;max-height:40%;overflow:auto;display:none;border-top:1px solid #fecaca}</style>
<script>(function(){function show(t){var d=document.getElementById('__artifact_err');if(!d){d=document.createElement('pre');d.id='__artifact_err';(document.body||document.documentElement).appendChild(d)}d.style.display='block';d.textContent+=t+'\\n'}
window.__artifactError=show;window.addEventListener('error',function(e){show((e.error&&e.error.stack)||e.message)});window.addEventListener('unhandledrejection',function(e){show('Promise: '+((e.reason&&e.reason.stack)||e.reason))});})();</script>`;

const HEAD_PRELUDE = `${STORAGE_SHIM}${ERROR_OVERLAY}`;

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Insere o prelúdio logo no início do <head> (ou do documento). */
function withPrelude(html: string): string {
  const head = html.match(/<head[^>]*>/i);
  if (head && head.index !== undefined) {
    const at = head.index + head[0].length;
    return html.slice(0, at) + HEAD_PRELUDE + html.slice(at);
  }
  const htmlTag = html.match(/<html[^>]*>/i);
  if (htmlTag && htmlTag.index !== undefined) {
    const at = htmlTag.index + htmlTag[0].length;
    return `${html.slice(0, at)}<head>${HEAD_PRELUDE}</head>${html.slice(at)}`;
  }
  const doctype = html.match(/^\s*<!doctype[^>]*>/i);
  if (doctype) {
    return doctype[0] + HEAD_PRELUDE + html.slice(doctype[0].length);
  }
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">${HEAD_PRELUDE}</head><body>${html}</body></html>`;
}

const HOOKS = 'useState,useEffect,useRef,useMemo,useCallback,useReducer,useContext,useLayoutEffect,' +
  'useImperativeHandle,useId,useTransition,useDeferredValue,createContext,Fragment,forwardRef,memo,' +
  'createElement,Children,cloneElement,isValidElement,StrictMode';

const NS_IMPORT = /import\s+\*\s+as\s+([A-Za-z0-9_$]+)\s+from\s*['"]([^'"]+)['"];?/g;
const IMPORT = /import\s+(?:([A-Za-z0-9_$]+)\s*,?\s*)?(?:\{([^}]*)\})?\s*from\s*['"]([^'"]+)['"];?/g;
const SIDE_IMPORT = /import\s*['"][^'"]+['"];?/g;

function importNames(def: string | undefined, named: string | undefined): Array<[string, string]> {
  // [nome local, nome exportado]
  const out: Array<[string, string]> = [];
  if (def && def.trim()) out.push([def.trim(), 'default']);
  (named || '').split(',').forEach((part) => {
    const p = part.trim().replace(/^type\s+/, '');
    if (!p) return;
    const [orig, local] = p.split(/\s+as\s+/).map((x) => x.trim());
    out.push([local || orig, orig]);
  });
  return out;
}

/** Transforma o módulo do componente num script executável pelo Babel standalone. */
export function reactModuleToScript(src: string): string {
  const consts: string[] = [];
  let body = src.replace(NS_IMPORT, (_m, ns: string, mod: string) => {
    if (mod === 'react' || mod.startsWith('react-dom')) {
      if (mod === 'react') consts.push(`const ${ns}=React;`);
      return '';
    }
    consts.push(mod === 'recharts' ? `const ${ns}=window.Recharts||__stub;` : `const ${ns}=__stub;`);
    return '';
  });
  body = body.replace(IMPORT, (_m, def: string, named: string, mod: string) => {
    for (const [local, orig] of importNames(def, named)) {
      if (mod === 'react') {
        if (orig === 'default') { if (local !== 'React') consts.push(`const ${local}=React;`); }
        else if (!HOOKS.split(',').includes(local)) consts.push(`const ${local}=React.${orig};`);
        else if (local !== orig) consts.push(`const ${local}=React.${orig};`);
      } else if (mod.startsWith('react-dom')) {
        if (local !== 'ReactDOM') consts.push(`const ${local}=ReactDOM;`);
      } else if (mod === 'recharts') {
        consts.push(`const ${local}=(window.Recharts&&window.Recharts.${orig === 'default' ? 'default' : orig})||__stub;`);
      } else if (['cn', 'clsx', 'classNames', 'classnames', 'twMerge'].includes(local)) {
        consts.push(`const ${local}=(...a)=>a.flat(Infinity).filter(Boolean).join(' ');`);
      } else {
        consts.push(`const ${local}=__stub;`);
      }
    }
    return '';
  });
  body = body.replace(SIDE_IMPORT, '');
  body = body
    .replace(/export\s+default\s+function\s+([A-Za-z0-9_$]+)/, 'window.__APP=function $1')
    .replace(/export\s+default\s+class\s+([A-Za-z0-9_$]+)/, 'window.__APP=class $1')
    .replace(/export\s+default\s+/, 'window.__APP=')
    .replace(/export\s+(const|let|var|function|class)\s+/g, '$1 ');
  return `const {${HOOKS}}=React;
function __mk(){const f=function(p){return p&&p.children!==undefined?p.children:null;};return new Proxy(f,{get(t,k){return (k in t)?t[k]:__mk();},apply(){return null;}});}
const __stub=__mk();
${consts.join('\n')}
${body}`;
}

/** JSON seguro dentro de <script>: nenhum "</script" ou "<!--" sobrevive. */
export function scriptSafeJson(value: unknown): string {
  return JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

function reactDocument(src: string): string {
  // O fonte vai como string JSON e é compilado explicitamente (TSX: tipos e
  // `as const` são removidos). Erro de sintaxe vira mensagem na faixa de erro
  // em vez de quebrar o documento; o código compilado roda num <script> próprio.
  const source = scriptSafeJson(reactModuleToScript(src));
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">
${HEAD_PRELUDE}
<script src="${TAILWIND}"></script>
<script crossorigin src="${REACT}"></script>
<script crossorigin src="${REACT_DOM}"></script>
<script crossorigin src="${PROP_TYPES}"></script>
<script crossorigin src="${RECHARTS}"></script>
<script crossorigin src="${BABEL}"></script>
<style>html,body{margin:0}body{font-family:system-ui,-apple-system,'Segoe UI',sans-serif}</style>
</head><body><div id="root"></div>
<script>
(function () {
  var code;
  try {
    code = Babel.transform(${source}, {
      filename: 'artifact.tsx',
      sourceType: 'script',
      presets: [['typescript', { isTSX: true, allExtensions: true }], 'react'],
    }).code;
  } catch (e) {
    window.__artifactError('Erro de sintaxe: ' + ((e && e.message) || e));
    return;
  }
  var s = document.createElement('script');
  s.textContent = code + '\\n;(function(){if(!window.__APP){window.__artifactError("O componente não tem export default.");return;}' +
    'ReactDOM.createRoot(document.getElementById("root")).render(React.createElement(window.__APP));})();';
  document.body.appendChild(s);
})();
</script></body></html>`;
}

function svgDocument(src: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">${HEAD_PRELUDE}
<style>html,body{margin:0;height:100%}body{display:flex;align-items:center;justify-content:center;background:#fff}svg{max-width:100%;max-height:100vh;height:auto}</style>
</head><body>${src}</body></html>`;
}

function mermaidDocument(src: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">${HEAD_PRELUDE}
<script src="${MERMAID}"></script>
<style>body{margin:0;padding:16px;display:flex;justify-content:center;background:#fff;font-family:system-ui,sans-serif}.mermaid{max-width:100%}</style>
</head><body><pre class="mermaid">${escapeHtml(src)}</pre>
<script>try{mermaid.initialize({startOnLoad:false,securityLevel:'strict',theme:'default'});mermaid.run().catch(function(e){window.__artifactError(String(e&&e.message||e))});}catch(e){window.__artifactError(String(e))}</script>
</body></html>`;
}

/** Documento completo para o srcdoc do iframe. */
export function buildArtifactDocument(type: string, content: string): string {
  switch (type) {
    case 'react':
      return reactDocument(content);
    case 'svg':
      return svgDocument(content);
    case 'mermaid':
      return mermaidDocument(content);
    case 'html':
    default:
      return withPrelude(content);
  }
}

export const ARTIFACT_TYPE_LABELS: Record<string, string> = {
  html: 'Página HTML',
  react: 'Componente React',
  svg: 'Imagem SVG',
  mermaid: 'Diagrama',
};

/** Extensão e MIME do download do código-fonte. */
export function artifactDownload(type: string): { ext: string; mime: string } {
  switch (type) {
    case 'react':
      return { ext: 'jsx', mime: 'text/javascript' };
    case 'svg':
      return { ext: 'svg', mime: 'image/svg+xml' };
    case 'mermaid':
      return { ext: 'mmd', mime: 'text/plain' };
    default:
      return { ext: 'html', mime: 'text/html' };
  }
}

export function slugifyFilename(title: string): string {
  const base = (title || 'artefato')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return base.slice(0, 60) || 'artefato';
}
